/**
 * Bundle-Kopplung: Portal-Paket ⇄ Einrichtungsgebühr
 *
 * Hält im Warenkorb die feste Kopplung zwischen einem Portal-Paket und der
 * globalen Einrichtungsgebühr aufrecht — unabhängig davon, über welchen Weg
 * ein Portal hinzugefügt wurde (Kalkulator, Produktseite, Quick-Add, Drawer).
 *
 * Zusätzlich gilt die Invariante: NUR EIN Portal-Paket im Warenkorb.
 *  - Zweiter Add derselben Variante  → stilles Duplikat, wird entfernt.
 *  - Zweiter Add einer ANDEREN Variante → Rückfrage per Modal
 *    („bereits im Warenkorb — ersetzen?"). Bestätigt der Nutzer, gewinnt das
 *    neue Paket (altes Portal + dessen Gebühr werden entfernt); bricht er ab,
 *    wird der Neuzugang entfernt und das bestehende Portal bleibt.
 *  - Alt-Warenkörbe mit mehreren etablierten Portalen werden still auf eines
 *    reduziert (neuestes gewinnt).
 *
 * Mechanik über die Line-Item-Property `_bundle_id`:
 *  - Portal OHNE _bundle_id  → frisch hinzugefügt (z.B. Produktseite/Quick-Add):
 *      Bundle etablieren (Menge auf 1, _bundle_id setzen, Einrichtungsgebühr ergänzen).
 *  - Portal MIT _bundle_id, aber ohne passende Einrichtungsgebühr:
 *      Service wurde entfernt → Portal ebenfalls entfernen.
 *  - Einrichtungsgebühr ohne gültig gekoppeltes Portal → entfernen.
 *  - Portal-Menge wird hart auf 1 gehalten, Einrichtungsgebühr ebenso.
 *
 * Zusätzlich trägt jede Portal-Zeile den Umfang als sichtbare Line-Item-Property
 * (`Umfang: pauschal bis zu 5 WE`) — der Varianten-Titel allein („Vermieter") sagt
 * nichts über die Anzahl der Wohneinheiten. Das passiert bewusst HIER und nicht beim
 * Hinzufügen: Properties nimmt Shopify nur beim /cart/add entgegen, also müsste sonst
 * jeder Kaufweg den Text selbst mitschicken (Produktseite, Kalkulator, Quick-Add,
 * Drawer) und man hätte vier Quellen für denselben Satz. Der reconcile unten läuft
 * ohnehin bei jedem cart:update und bei jedem Seitenaufruf über genau diese Zeilen.
 *
 * Konfiguration via window.BundleConfig (in theme.liquid aus den Theme-Settings
 * gerendert): { serviceVariantId, portalVariantIds: [...], portalScopeKey,
 * portalScopes: { variantId: text }, labels: {...} }. Ohne vollständige
 * Konfiguration ist das Modul inaktiv.
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const cfg = window.BundleConfig || {};
const SERVICE_ID = Number(cfg.serviceVariantId) || null;
const PORTAL_IDS = new Set((cfg.portalVariantIds || []).map(Number));
const SOURCE = 'bundle-coupling';

// Sichtbarer Umfang-Zusatz je Portal-Variante. Leerer Key = Funktion aus.
const SCOPE_KEY = (cfg.portalScopeKey || '').trim();
/** @type {Record<string, string>} */
const SCOPES = cfg.portalScopes || {};

// Modal-Texte (aus theme.liquid gerendert; deutsche Defaults als Fallback)
const LABELS = Object.assign(
  {
    replaceTitle: 'Portalpaket ersetzen?',
    replaceBody:
      'In deinem Warenkorb liegt bereits das Portalpaket „{existing}“. Ein zweites Portalpaket ist nicht möglich. Möchtest du es durch „{incoming}“ ersetzen?',
    replaceConfirm: 'Ersetzen',
    replaceCancel: 'Abbrechen',
  },
  cfg.labels || {}
);

if (SERVICE_ID && PORTAL_IDS.size > 0) {
  // Theme.routes wird in snippets/scripts.liquid als globale const deklariert.
  const routes = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
  const ADD_URL = routes.cart_add_url || '/cart/add.js';
  const CHANGE_URL = routes.cart_change_url || '/cart/change.js';
  const CART_URL = (routes.cart_url || '/cart') + '.js';
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  let busy = false;
  let pending = false;

  const makeId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'bc-' + Date.now().toString(36) + '-' + Math.round(performance.now());

  const isPortal = (item) => PORTAL_IDS.has(item.variant_id);
  const isService = (item) => item.variant_id === SERVICE_ID;
  const bundleIdOf = (item) => (item.properties && item.properties._bundle_id) || null;

  /**
   * Properties der Zeile plus Umfang-Zusatz. Kopiert immer, mutiert nie das
   * Cart-Objekt. Fehlt ein Eintrag in SCOPES (Metafelder nicht gepflegt), wird ein
   * evtl. vorhandener Zusatz entfernt statt eine leere Zeile stehen zu lassen.
   * @param {any} item
   * @returns {Record<string, string>}
   */
  const withScope = (item) => {
    const props = Object.assign({}, item.properties);
    if (!SCOPE_KEY) return props;
    const text = SCOPES[String(item.variant_id)] || '';
    if (text) props[SCOPE_KEY] = text;
    else delete props[SCOPE_KEY];
    return props;
  };

  /**
   * Property-Vergleich, damit reconcile nur schreibt, wenn sich wirklich etwas
   * ändert. Ohne das würde jeder Durchlauf ein cart/change auslösen und über das
   * eigene cart:update-Event den nächsten Durchlauf anstoßen.
   * @param {Record<string, any> | null | undefined} current
   * @param {Record<string, any>} wanted
   * @returns {boolean}
   */
  const samePropsAs = (current, wanted) => {
    const now = current || {};
    const nowKeys = Object.keys(now);
    const wantedKeys = Object.keys(wanted);
    if (nowKeys.length !== wantedKeys.length) return false;
    return wantedKeys.every((key) => String(now[key] ?? '') === String(wanted[key] ?? ''));
  };

  /**
   * Menschlich lesbarer Paketname für die Rückfrage.
   * @param {any} item
   * @returns {string}
   */
  const displayName = (item) => {
    const vt = item.variant_title;
    if (vt && vt !== 'Default Title') return `${item.product_title} – ${vt}`;
    return item.product_title || item.title || 'Portalpaket';
  };

  async function getCart() {
    const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function changeLine(key, quantity, properties) {
    const body = { id: key, quantity };
    if (properties) body.properties = properties;
    const res = await fetch(CHANGE_URL, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
    if (!res.ok) throw new Error('cart/change fehlgeschlagen: HTTP ' + res.status);
  }

  async function addLine(id, quantity, properties) {
    const res = await fetch(ADD_URL, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id, quantity, properties }] }),
    });
    if (!res.ok) throw new Error('cart/add fehlgeschlagen: HTTP ' + res.status);
  }

  /**
   * Entfernt ein Portal und die per _bundle_id gekoppelte Einrichtungsgebühr.
   * @param {any} portal
   * @param {any[]} services
   */
  async function removeBundle(portal, services) {
    await changeLine(portal.key, 0);
    const bid = bundleIdOf(portal);
    if (bid) {
      const svc = services.find((s) => bundleIdOf(s) === bid);
      if (svc) await changeLine(svc.key, 0);
    }
  }

  /**
   * Erzwingt die Single-Portal-Invariante. Zeigt bei einer anderen, frisch
   * hinzugefügten Variante die Ersetzen-Rückfrage. Läuft synchron innerhalb
   * des reconcile-busy-Locks — solange das Modal offen ist, werden weitere
   * cart:update-Events über `pending` aufgeschoben.
   *
   * @param {any[]} portals
   * @param {any[]} services
   * @returns {Promise<boolean>} true, wenn der Warenkorb verändert wurde
   */
  async function enforceSinglePortal(portals, services) {
    let changed = false;
    const marked = portals.filter((p) => bundleIdOf(p));
    const fresh = portals.filter((p) => !bundleIdOf(p));

    // Kein etabliertes Portal → mehrere frische gleichzeitig: neuestes behalten.
    if (marked.length === 0) {
      const keep = fresh[fresh.length - 1];
      for (const p of fresh) {
        if (p !== keep) {
          await changeLine(p.key, 0);
          changed = true;
        }
      }
      return changed;
    }

    // Das (neueste) etablierte Portal ist der Bestandsstand.
    const existing = marked[marked.length - 1];

    // Überzählige etablierte Portale (Alt-Warenkörbe) still entfernen inkl. Gebühr.
    for (const p of marked) {
      if (p === existing) continue;
      await removeBundle(p, services);
      changed = true;
    }

    // Frische Zugänge klassifizieren.
    const duplicates = fresh.filter((f) => f.variant_id === existing.variant_id);
    const differing = fresh.filter((f) => f.variant_id !== existing.variant_id);

    // Echte Duplikate (identische Variante) still entfernen.
    for (const d of duplicates) {
      await changeLine(d.key, 0);
      changed = true;
    }

    if (differing.length === 0) return changed;

    // Nur EIN Neuzugang kommt zur Entscheidung; weitere abweichende still entfernen.
    const incoming = differing[differing.length - 1];
    for (const d of differing) {
      if (d !== incoming) {
        await changeLine(d.key, 0);
        changed = true;
      }
    }

    // Rückfrage: bestehendes Portal durch den Neuzugang ersetzen?
    const replace = await confirmReplace(displayName(existing), displayName(incoming));
    if (replace) {
      // Neues gewinnt: bestehendes Portal + dessen Gebühr entfernen. Der Neuzugang
      // bleibt unmarkiert und wird in der nachfolgenden Kopplungsphase etabliert.
      await removeBundle(existing, services);
    } else {
      // Abbruch: Neuzugang entfernen, bestehendes Portal bleibt unverändert.
      await changeLine(incoming.key, 0);
    }
    return true;
  }

  async function reconcile() {
    if (busy) {
      pending = true;
      return;
    }
    busy = true;

    try {
      let changed = false;
      let cart = await getCart();
      let items = cart.items || [];
      let portals = items.filter(isPortal);
      let services = items.filter(isService);

      // 0) Single-Portal-Invariante (inkl. evtl. Ersetzen-Rückfrage) zuerst.
      if (portals.length > 1) {
        const enforced = await enforceSinglePortal(portals, services);
        if (enforced) {
          changed = true;
          // Frisch einlesen — die Kopplungsphase arbeitet auf dem neuen Stand.
          cart = await getCart();
          items = cart.items || [];
          portals = items.filter(isPortal);
          services = items.filter(isService);
        }
      }

      // Bundle-IDs aus dem Snapshot VOR Mutationen — die Invarianten beziehen
      // sich immer auf diesen konsistenten Stand.
      const serviceBids = new Set(services.map(bundleIdOf).filter(Boolean));
      const portalBids = new Set(portals.map(bundleIdOf).filter(Boolean));

      // 1) Portale verarbeiten
      for (const p of portals) {
        const bid = bundleIdOf(p);
        if (!bid) {
          // Frisch hinzugefügt → Bundle etablieren. WICHTIG: Erst die
          // Einrichtungsgebühr hinzufügen, DANACH die _bundle_id ans Portal
          // schreiben. Andernfalls würde ein fehlgeschlagener Service-Add
          // (z.B. ausverkauft/422) ein Portal mit _bundle_id, aber ohne Service
          // hinterlassen — der nächste reconcile würde dieses Portal löschen
          // (stiller Datenverlust). Schlägt addLine fehl, bleibt das Portal
          // unmarkiert und wird beim nächsten reconcile erneut versucht.
          const newId = makeId();
          await addLine(SERVICE_ID, 1, { _bundle_id: newId, _bundle_role: 'setup' });
          await changeLine(
            p.key,
            1,
            Object.assign(withScope(p), { _bundle_id: newId, _bundle_role: 'portal' })
          );
          changed = true;
        } else if (!serviceBids.has(bid)) {
          // War gebündelt, Service fehlt jetzt → Service wurde entfernt → Portal entfernen
          await changeLine(p.key, 0);
          changed = true;
        } else {
          // Portal-Menge fest auf 1, und der Umfang-Zusatz wird nachgezogen — greift
          // damit auch für Warenkörbe, die vor dieser Funktion angelegt wurden, und
          // für Kaufwege, die selbst keine Properties mitschicken. Properties immer
          // explizit mitsenden, damit die _bundle_id garantiert erhalten bleibt
          // (nicht auf Shopifys Property-Erhaltung bei change.js verlassen).
          const wanted = withScope(p);
          if (p.quantity !== 1 || !samePropsAs(p.properties, wanted)) {
            await changeLine(p.key, 1, wanted);
            changed = true;
          }
        }
      }

      // 2) Einrichtungsgebühr-Zeilen bereinigen
      for (const s of services) {
        const bid = bundleIdOf(s);
        const validlyCoupled = bid && portalBids.has(bid);
        if (!validlyCoupled) {
          // Verwaiste / ungekoppelte Gebühr (z.B. allein über Produktseite hinzugefügt)
          await changeLine(s.key, 0);
          changed = true;
        } else if (s.quantity !== 1) {
          await changeLine(s.key, 1, s.properties);
          changed = true;
        }
      }

      if (changed) {
        const fresh = await getCart();
        document.dispatchEvent(
          new CartUpdateEvent(fresh, SOURCE, { source: SOURCE, itemCount: fresh.item_count })
        );
      }
    } catch (err) {
      console.warn('[BundleCoupling] reconcile fehlgeschlagen:', err);
    } finally {
      busy = false;
      if (pending) {
        pending = false;
        reconcile();
      }
    }
  }

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    // Eigene Updates nicht erneut verarbeiten (verhindert Endlosschleife)
    if (event && event.detail && event.detail.data && event.detail.data.source === SOURCE) return;
    reconcile();
  });

  // Direktaufruf der Warenkorbseite o.ä. ebenfalls absichern
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => reconcile(), { once: true });
  } else {
    reconcile();
  }

  /* ─────────────────────────── Ersetzen-Modal ─────────────────────────── */

  /** @type {HTMLDialogElement | null} */
  let dialogEl = null;

  function ensureDialog() {
    if (dialogEl) return dialogEl;

    const style = document.createElement('style');
    style.textContent = `
      dialog.bc-dialog {
        border: none;
        margin: auto;
        padding: 0;
        border-radius: 16px;
        max-width: 440px;
        width: calc(100vw - 2rem);
        color: var(--text-main, #1a1a2e);
        background: var(--bg, #ffffff);
        box-shadow: 0 24px 60px rgba(20, 22, 40, 0.35);
      }
      dialog.bc-dialog::backdrop {
        background: rgba(20, 22, 40, 0.55);
        backdrop-filter: blur(2px);
      }
      .bc-dialog__inner { padding: 1.75rem 1.75rem 1.5rem; }
      .bc-dialog__title {
        margin: 0 0 0.75rem;
        font-size: 1.25rem;
        line-height: 1.25;
        color: var(--primary, #363751);
      }
      .bc-dialog__body { margin: 0 0 1.5rem; font-size: 1.0625rem; line-height: 1.55; }
      .bc-dialog__actions { display: flex; gap: 0.75rem; flex-wrap: wrap; }
      .bc-dialog__btn {
        flex: 1 1 auto;
        min-height: 48px;
        padding: 0.75rem 1.25rem;
        border-radius: 10px;
        font-size: 1.0625rem;
        font-weight: 600;
        cursor: pointer;
        border: 2px solid transparent;
        transition: filter 0.15s ease, background-color 0.15s ease;
      }
      .bc-dialog__btn--confirm {
        background: var(--primary, #363751);
        color: var(--primary-light, #4dd9ec);
      }
      .bc-dialog__btn--confirm:hover { filter: brightness(1.12); }
      .bc-dialog__btn--cancel {
        background: transparent;
        color: var(--text-main, #1a1a2e);
        border-color: var(--border, #dde3ee);
      }
      .bc-dialog__btn--cancel:hover { background: var(--bg, #eef2f7); }
      .bc-dialog__btn:focus-visible {
        outline: 3px solid var(--primary-light, #4dd9ec);
        outline-offset: 2px;
      }
    `;
    document.head.appendChild(style);

    dialogEl = document.createElement('dialog');
    dialogEl.className = 'bc-dialog';
    dialogEl.innerHTML = `
      <div class="bc-dialog__inner">
        <h2 class="bc-dialog__title" data-bc-title></h2>
        <p class="bc-dialog__body" data-bc-body></p>
        <div class="bc-dialog__actions">
          <button type="button" class="bc-dialog__btn bc-dialog__btn--cancel" data-bc-cancel></button>
          <button type="button" class="bc-dialog__btn bc-dialog__btn--confirm" data-bc-confirm></button>
        </div>
      </div>
    `;
    document.body.appendChild(dialogEl);
    return dialogEl;
  }

  /**
   * Zeigt die Ersetzen-Rückfrage und löst zu true (ersetzen) / false (abbrechen) auf.
   * @param {string} existingName
   * @param {string} incomingName
   * @returns {Promise<boolean>}
   */
  function confirmReplace(existingName, incomingName) {
    const dialog = ensureDialog();
    const titleEl = dialog.querySelector('[data-bc-title]');
    const bodyEl = dialog.querySelector('[data-bc-body]');
    const confirmBtn = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-bc-confirm]'));
    const cancelBtn = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-bc-cancel]'));

    if (titleEl) titleEl.textContent = LABELS.replaceTitle;
    if (bodyEl) {
      bodyEl.textContent = LABELS.replaceBody
        .replace('{existing}', existingName)
        .replace('{incoming}', incomingName);
    }
    confirmBtn.textContent = LABELS.replaceConfirm;
    cancelBtn.textContent = LABELS.replaceCancel;

    return new Promise((resolve) => {
      let settled = false;
      const finish = (result) => {
        if (settled) return;
        settled = true;
        confirmBtn.removeEventListener('click', onConfirm);
        cancelBtn.removeEventListener('click', onCancel);
        dialog.removeEventListener('cancel', onEscape);
        if (dialog.open) dialog.close();
        resolve(result);
      };
      const onConfirm = () => finish(true);
      const onCancel = () => finish(false);
      // ESC => Abbruch (bestehendes Portal bleibt). Ein Backdrop-Klick schließt
      // den nativen Dialog bewusst NICHT — die Wahl soll explizit erfolgen.
      const onEscape = (e) => {
        e.preventDefault();
        finish(false);
      };

      confirmBtn.addEventListener('click', onConfirm);
      cancelBtn.addEventListener('click', onCancel);
      dialog.addEventListener('cancel', onEscape);

      dialog.showModal();
      confirmBtn.focus();
    });
  }
}
